import { useCallback } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import type { EisApiClients } from './clients';
import { apiKeys } from './keys';
import { isApiError, unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * BACKGROUND JOBS (SDD-020 §6, §9; T-43): exports and imports that may outlive a request, one
 * `Job` resource per service, the same message in both. The console never addresses a job by path:
 * the `service` argument picks the SDK (`insurance` → the instance client, `billing` → the billing
 * client), and each client knows its own prefix (prompt 28 Decision 1).
 *
 * Polling, never a push channel (J-4): an open job is read every second, backing off to five, and
 * not at all once it is final; the tray reads the caller's lists only while a job is active or the
 * tray is open. Every create carries an `Idempotency-Key`, so the SDK's session middleware may
 * retry it after a dropped connection and the service answers the job the first attempt made
 * (SDD-019 S-6).
 */

/**
 * The service a job lives in. `data` is the Data Suite's service (SDD-024 E-1, W-6): report exports
 * and catalog refreshes, started by its own RPCs rather than by the list-export ones.
 */
export type JobService = 'insurance' | 'billing' | 'data';

/** The services whose LIST and DETAIL exports this layer starts (SDD-020 §8). */
export type ExportService = Exclude<JobService, 'data'>;

/** One job, as both services answer it (`common.jobs.v1.Job`). */
export type Job = InstanceApi.Job;

export type JobState = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
export type JobKind = 'export.list' | 'export.detail' | 'import.leads';

const FINAL: readonly string[] = ['SUCCEEDED', 'FAILED', 'CANCELLED', 'EXPIRED'];

/** A job never leaves a final state except to EXPIRED. */
export function isFinalJob(job: Pick<Job, 'state'> | undefined): boolean {
  return job?.state !== undefined && FINAL.includes(job.state);
}

/** Active = the tray shows a progress bar and keeps polling. */
export function isActiveJob(job: Pick<Job, 'state'> | undefined): boolean {
  return job?.state === 'QUEUED' || job?.state === 'RUNNING';
}

/**
 * The poll interval after `polls` reads of an open job: 1 s, then ×1.5 per read, at most 5 s (J-4).
 * Pure, so the backoff is a unit test rather than a timing test.
 */
export function pollInterval(polls: number): number {
  return Math.min(5000, Math.round(1000 * 1.5 ** Math.max(0, polls)));
}

/** A job with the service it came from (the tray merges both services' lists). */
export interface ServiceJob {
  readonly service: JobService;
  readonly job: Job;
}

/**
 * The caller's jobs of both services, newest first, at most `limit` (SDD-020 §9: the last 20).
 * Pure: the merge is a unit test.
 */
export function mergeJobs(
  insurance: readonly Job[] | undefined,
  billing: readonly Job[] | undefined,
  limit = 20,
  data?: readonly Job[],
): ServiceJob[] {
  const all: ServiceJob[] = [
    ...(insurance ?? []).map((job) => ({ service: 'insurance' as const, job })),
    ...(billing ?? []).map((job) => ({ service: 'billing' as const, job })),
    ...(data ?? []).map((job) => ({ service: 'data' as const, job })),
  ];
  all.sort((a, b) => (b.job.createdAt ?? '').localeCompare(a.job.createdAt ?? ''));
  return all.slice(0, limit);
}

/** A fresh idempotency key for one user action. */
export function newIdempotencyKey(): string {
  const random = globalThis.crypto as Crypto | undefined;
  if (random !== undefined && typeof random.randomUUID === 'function') return random.randomUUID();
  return `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

function idempotencyHeaders(key: string): Record<string, string> {
  return { 'Idempotency-Key': key };
}

async function readJob(clients: EisApiClients, service: JobService, id: string): Promise<Job> {
  if (service === 'data') {
    const data = await unwrap(
      clients.data.client.GET('/dataservice/v1/jobs/{id}', { params: { path: { id } } }),
    );
    return data.job as Job;
  }
  if (service === 'billing') {
    const data = await unwrap(
      clients.billing.client.GET('/billingservice/v1/jobs/{id}', { params: { path: { id } } }),
    );
    return data.job as Job;
  }
  const data = await unwrap(clients.instance.client.GET('/v1/jobs/{id}', { params: { path: { id } } }));
  return data.job as Job;
}

/**
 * One job, polled while it is open: 1 s backing off to 5 s, stopping at a final state (J-4).
 * `id` undefined reads nothing (the dialog has not started a job yet).
 */
export function useJob(service: JobService, id: string | undefined): UseQueryResult<Job> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.jobs.job(service, id ?? ''),
    queryFn: () => readJob(clients, service, id ?? ''),
    enabled: id !== undefined && id !== '',
    refetchInterval: (query) =>
      isFinalJob(query.state.data) ? false : pollInterval(query.state.dataUpdateCount),
  });
}

async function listMine(clients: EisApiClients, service: JobService): Promise<Job[]> {
  try {
    if (service === 'data') {
      const data = await unwrap(
        clients.data.client.GET('/dataservice/v1/jobs', { params: { query: { pageSize: 20 } } }),
      );
      return data.items ?? [];
    }
    if (service === 'billing') {
      const data = await unwrap(
        clients.billing.client.GET('/billingservice/v1/jobs', { params: { query: { pageSize: 20 } } }),
      );
      return (data.items ?? []);
    }
    const data = await unwrap(clients.instance.client.GET('/v1/jobs', { params: { query: { pageSize: 20 } } }));
    return (data.items ?? []);
  } catch (error) {
    // A persona that reaches no part of one service (403) has no jobs there — the tray is empty
    // for it, never broken (SDD-020 §10: the tray is everyone's).
    if (isApiError(error) && (error.status === 403 || error.status === 404)) return [];
    throw error;
  }
}

export interface MyJobs {
  readonly jobs: ServiceJob[];
  /** How many of them are queued or running (the tray's badge). */
  readonly active: number;
  readonly isLoading: boolean;
}

/**
 * The caller's last 20 jobs of both services (SDD-020 §9). Polls only while a job is active or
 * `open` (the tray's popover) — an idle console sends nothing (J-4).
 */
export function useMyJobs({ open = false }: { readonly open?: boolean } = {}): MyJobs {
  const clients = useApiClients();
  const interval = (jobs: readonly Job[] | undefined): number | false =>
    open || (jobs ?? []).some((job) => isActiveJob(job)) ? 2000 : false;
  const insurance = useQuery({
    queryKey: apiKeys.jobs.mine('insurance'),
    queryFn: () => listMine(clients, 'insurance'),
    refetchInterval: (query) => interval(query.state.data),
  });
  const billing = useQuery({
    queryKey: apiKeys.jobs.mine('billing'),
    queryFn: () => listMine(clients, 'billing'),
    refetchInterval: (query) => interval(query.state.data),
  });
  // The Data Suite's jobs (SDD-024 E-1): report exports and catalog refreshes in the same tray.
  const data = useQuery({
    queryKey: apiKeys.jobs.mine('data'),
    queryFn: () => listMine(clients, 'data'),
    refetchInterval: (query) => interval(query.state.data),
  });
  const jobs = mergeJobs(insurance.data, billing.data, 20, data.data);
  return {
    jobs,
    active: jobs.filter((entry) => isActiveJob(entry.job)).length,
    isLoading: insurance.isLoading || billing.isLoading || data.isLoading,
  };
}

/**
 * The export's `query` from the list's own (SDD-020 §8.2): the same filters, search and order,
 * and the cut the screen shows (`validAt`, `asOf`) — the file is the list the user is looking at,
 * not the list a second later. Empty parts are left out.
 */
export function exportQueryOf(
  query: { readonly filters?: string; readonly search?: string; readonly order?: string },
  cut?: { readonly validAt?: string; readonly asOf?: string },
): NonNullable<InstanceApi.CreateExportJobRequest['query']> {
  const parts: Record<string, string | undefined> = {
    filters: query.filters,
    search: query.search,
    order: query.order,
    validAt: cut?.validAt,
    asOf: cut?.asOf,
  };
  return Object.fromEntries(
    Object.entries(parts).filter((entry): entry is [string, string] => (entry[1] ?? '') !== ''),
  );
}

/** A list export (SDD-020 §8.2): the list's own query plus the visible columns. */
export type ExportVariables = InstanceApi.CreateExportJobRequest;
/** A detail export (SDD-020 §8.3): one resource, translated sheet names and labels. */
export type DetailExportVariables = InstanceApi.CreateDetailExportJobRequest;

/** What a create answers: the job — finished within `wait` (download it) or still running. */
export interface CreatedJob {
  readonly service: JobService;
  readonly job: Job;
}

/** Starts a list export of a service (J-3: one call, small or big). */
export function useCreateExport(service: ExportService): ServiceMutation<ExportVariables, CreatedJob> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async (variables: ExportVariables) => {
      const headers = idempotencyHeaders(newIdempotencyKey());
      const data =
        service === 'billing'
          ? await unwrap(
              clients.billing.client.POST('/billingservice/v1/exports', {
                body: variables,
                headers,
              }),
            )
          : await unwrap(clients.instance.client.POST('/v1/exports', { body: variables, headers }));
      return { service, job: data.job as Job };
    },
    invalidates: () => [apiKeys.jobs.mine(service)],
  });
}

/** Starts a detail export (one XLSX workbook) of a service. */
export function useCreateDetailExport(
  service: ExportService,
): ServiceMutation<DetailExportVariables, CreatedJob> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async (variables: DetailExportVariables) => {
      const headers = idempotencyHeaders(newIdempotencyKey());
      const data =
        service === 'billing'
          ? await unwrap(
              clients.billing.client.POST('/billingservice/v1/exports:detail', {
                body: variables,
                headers,
              }),
            )
          : await unwrap(clients.instance.client.POST('/v1/exports:detail', { body: variables, headers }));
      return { service, job: data.job as Job };
    },
    invalidates: () => [apiKeys.jobs.mine(service)],
  });
}

export interface CancelJobVariables {
  readonly service: JobService;
  readonly id: string;
}

/** Cancels one of the caller's jobs: at once when queued, at the next page or row when running. */
export function useCancelJob(): ServiceMutation<CancelJobVariables, Job> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ service, id }: CancelJobVariables) => {
      if (service === 'data') {
        const data = await unwrap(
          clients.data.client.POST('/dataservice/v1/jobs/{id}:cancel', {
            params: { path: { id } },
            body: {},
          }),
        );
        return data.job as Job;
      }
      if (service === 'billing') {
        const data = await unwrap(
          clients.billing.client.POST('/billingservice/v1/jobs/{id}:cancel', { params: { path: { id } }, body: {} }),
        );
        return data.job as Job;
      }
      const data = await unwrap(
        clients.instance.client.POST('/v1/jobs/{id}:cancel', { params: { path: { id } }, body: {} }),
      );
      return data.job as Job;
    },
    invalidates: ({ service, id }) => [apiKeys.jobs.mine(service), apiKeys.jobs.job(service, id)],
  });
}

/** Which file of a job: the export (`output`) or an import's error file (`errors`). */
export type JobArtifact = 'output' | 'errors';

/** A signed link, 5 minutes (J-6). */
export interface JobDownload {
  readonly url: string;
  readonly filename: string;
}

/** Mints a download link for one of the caller's job files. */
export async function jobDownloadLink(
  clients: EisApiClients,
  service: JobService,
  id: string,
  artifact: JobArtifact = 'output',
): Promise<JobDownload> {
  const data =
    service === 'data'
      ? await unwrap(
          clients.data.client.GET('/dataservice/v1/jobs/{id}:download', {
            params: { path: { id }, query: { artifact } },
          }),
        )
      : service === 'billing'
      ? await unwrap(
          clients.billing.client.GET('/billingservice/v1/jobs/{id}:download', {
            params: { path: { id }, query: { artifact } },
          }),
        )
      : await unwrap(
          clients.instance.client.GET('/v1/jobs/{id}:download', { params: { path: { id }, query: { artifact } } }),
        );
  return { url: data.download?.url ?? '', filename: data.download?.filename ?? '' };
}

/**
 * Starts the browser's download of a signed link: a hidden anchor, navigated (§9). The file comes
 * straight from storage; the service never proxies it.
 */
export function startDownload(download: JobDownload): void {
  if (download.url === '' || typeof document === 'undefined') return;
  const anchor = document.createElement('a');
  anchor.href = download.url;
  anchor.download = download.filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Gets the link and starts the download (SDD-020 §9 `downloadJobArtifact`). */
export async function downloadJobArtifact(
  clients: EisApiClients,
  service: JobService,
  id: string,
  artifact: JobArtifact = 'output',
): Promise<JobDownload> {
  const download = await jobDownloadLink(clients, service, id, artifact);
  startDownload(download);
  return download;
}

/** The hook form of [downloadJobArtifact]. */
export function useDownloadJobArtifact(): (
  service: JobService,
  id: string,
  artifact?: JobArtifact,
) => Promise<JobDownload> {
  const clients = useApiClients();
  return useCallback(
    (service: JobService, id: string, artifact: JobArtifact = 'output') =>
      downloadJobArtifact(clients, service, id, artifact),
    [clients],
  );
}

/** GetLeadImportTemplate (SDD-020 §7.2): the context, the date, the format, the language. */
export interface LeadImportTemplateVariables {
  readonly parentPolicyCode?: string;
  readonly tarifErn?: string;
  readonly effectiveDate?: string;
  readonly format: 'XLSX' | 'CSV' | 'JSON_SCHEMA';
  readonly locale: 'de' | 'en';
}

/**
 * Downloads an import template derived from the tarif version (J-14) — a mutation, because it is
 * an action a button starts, and its answer is a link to follow, not data to cache.
 */
export function useLeadImportTemplate(): ServiceMutation<
  LeadImportTemplateVariables,
  InstanceApi.GetLeadImportTemplateResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: async (variables: LeadImportTemplateVariables) => {
      const data = await unwrap(
        instance.client.GET('/v1/leads:import-template', {
          params: {
            query: {
              format: variables.format,
              locale: variables.locale,
              ...(variables.parentPolicyCode === undefined ? {} : { parentPolicyCode: variables.parentPolicyCode }),
              ...(variables.tarifErn === undefined ? {} : { tarifErn: variables.tarifErn }),
              ...(variables.effectiveDate === undefined ? {} : { effectiveDate: variables.effectiveDate }),
            },
          },
        }),
      );
      startDownload({ url: data.download?.url ?? '', filename: data.download?.filename ?? '' });
      return data;
    },
    invalidates: () => [],
  });
}
